/*###########################################################################
// Animations
//#########################################################################*/

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import '../vendor/gsap/_blurPlugin'
import '../vendor/gsap/_splitText'
import { lightenColour } from '../utilities/_colours'
import { addElement } from '../utilities/_elements'

gsap.registerPlugin(SplitText)
gsap.registerPlugin(ScrollTrigger)

const animations = {
	config: {
		hFadeIn: '[h-fade-in]',
		tFadeIn: '[t-fade-in]',
		fadeIn: '[fade-in]',
		lineFadeIn: '[line-fade-in]',
		imgReveal: '[img-reveal]'
	},

	startOffset: 1.25,

	elements: {},

	pageChange: false,

	init(pageChange = false) {
		const self = this

		// Set page change state
		//
		self.pageChange = pageChange

		// Heading fade in
		//
		self.elements.hFadeIn = document.querySelectorAll(self.config.hFadeIn)
		if (self.elements.hFadeIn !== null) {
			for (let i = 0; i < self.elements.hFadeIn.length; i++) {
				const hFadeIn = self.elements.hFadeIn[i]

				hFadeIn.style.opacity = '1'

				self.handleHFadeIn(hFadeIn)
			}
		}

		// Text fade in
		//
		self.elements.tFadeIn = document.querySelectorAll(self.config.tFadeIn)
		if (self.elements.tFadeIn !== null) {
			for (let i = 0; i < self.elements.tFadeIn.length; i++) {
				const tFadeIn = self.elements.tFadeIn[i]

				tFadeIn.style.opacity = '1'

				self.handleTFadeIn(tFadeIn)
			}
		}

		// Fade In
		//
		self.elements.fadeIn = document.querySelectorAll(self.config.fadeIn)
		if (self.elements.fadeIn !== null) {
			for (let i = 0; i < self.elements.fadeIn.length; i++) {
				const fadeIn = self.elements.fadeIn[i]

				fadeIn.style.opacity = '1'

				self.handleFadeIn(fadeIn)
			}
		}

		// Line fade in
		//
		self.elements.lineFadeIn = document.querySelectorAll(self.config.lineFadeIn)
		if (self.elements.lineFadeIn !== null) {
			for (let i = 0; i < self.elements.lineFadeIn.length; i++) {
				const lineFadeIn = self.elements.lineFadeIn[i]

				self.handleLineFadeIn(lineFadeIn)
			}
		}

		// Image reveal
		//
		self.elements.imgReveal = document.querySelectorAll(self.config.imgReveal)
		if (self.elements.imgReveal !== null) {
			for (let i = 0; i < self.elements.imgReveal.length; i++) {
				const imgReveal = self.elements.imgReveal[i]

				self.handleImgReveal(imgReveal)
			}
		}
	},

	handleHFadeIn(hFadeIn) {
		const self = this

		const startOffset = hFadeIn.hasAttribute('start-offset') ? hFadeIn.getAttribute('start-offset') : self.startOffset

		if (hFadeIn !== null) {
			const scrollTriggerEl = hFadeIn.closest('[animation-group]') ?? hFadeIn

			const tlHFadeIn = gsap.timeline({
				scrollTrigger: {
					trigger: scrollTriggerEl,
					start: "top +=" + (window.innerHeight / startOffset) + " middle",
					end: "top center",
				}
			})

			const splitHFadeIn = new SplitText(hFadeIn, {
				type: "words, chars",
				smartWrap: true
			});

			window.addEventListener('resize', function() {
				splitHFadeIn.revert()
			})

			const hFadeInWords = splitHFadeIn.chars;

			tlHFadeIn.from(hFadeInWords, {
				duration: 1,
				delay: self.calculateDelay(hFadeIn),
				alpha: 0,
				x: window.innerWidth < 1024 ? -4 : -8,
				skewX: 1,
				ease: "power3.out",
				stagger: 0.05
			})
		}
	},

	handleTFadeIn(tFadeIn) {
		const self = this

		const startOffset = tFadeIn.hasAttribute('start-offset') ? tFadeIn.getAttribute('start-offset') : self.startOffset

		if (tFadeIn !== null) {

			const scrollTriggerEl = tFadeIn.closest('[animation-group]') ?? tFadeIn

			const tlTFadeIn = gsap.timeline({
				scrollTrigger: {
					trigger: scrollTriggerEl,
					start: "top +=" + (window.innerHeight / startOffset) + " middle",
					end: "top center",
				}
			})

			const splitTFadeIn = new SplitText(tFadeIn, {
				type: "lines"
			});

			window.addEventListener('resize', function() {
				splitTFadeIn.revert()
			})

			const tFadeInWords = splitTFadeIn.lines;

			tlTFadeIn.from(tFadeInWords, {
				duration: 1.25,
				delay: self.calculateDelay(tFadeIn),
				alpha: 0,
				y: window.innerWidth < 1024 ? -4 : -8,
				skewX: 2,
				ease: "power3.out",
				stagger: 0.05
			})
		}
	},

	handleFadeIn(fadeIn) {
		const self = this

		const startOffset = fadeIn.hasAttribute('start-offset') ? fadeIn.getAttribute('start-offset') : self.startOffset

		if (fadeIn !== null) {
			const scrollTriggerEl = fadeIn.closest('[animation-group]') ?? fadeIn

			const tlFadeIn = gsap.timeline({
				scrollTrigger: {
					trigger: scrollTriggerEl,
					start: "top +=" + (window.innerHeight / startOffset) + " center"
				}
			})

			if (fadeIn.getAttribute('dir') == 'fromRight') {
				tlFadeIn.from(fadeIn, {
					duration: 0.8,
					delay: self.calculateDelay(fadeIn),
					alpha: 0,
					x: window.innerWidth < 1024 ? 4 : 8,
					ease: "power3.out"
				})
			} else if (fadeIn.getAttribute('dir') == 'fromLeft') {
				tlFadeIn.from(fadeIn, {
					duration: 0.8,
					delay: self.calculateDelay(fadeIn),
					alpha: 0,
					x: window.innerWidth < 1024 ? -4 : -8,
					ease: "power3.out"
				})
			} else if (fadeIn.getAttribute('dir') == 'fromBottom') {
				tlFadeIn.from(fadeIn, {
					duration: 0.8,
					delay: self.calculateDelay(fadeIn),
					alpha: 0,
					y: window.innerWidth < 1024 ? 4 : 8,
					ease: "power3.out"
				})
			} else if (fadeIn.getAttribute('dir') == 'none') {
				tlFadeIn.from(fadeIn, {
					duration: 0.8,
					delay: self.calculateDelay(fadeIn),
					alpha: 0,
					ease: "power3.out"
				})
			} else {
				tlFadeIn.from(fadeIn, {
					duration: 0.8,
					delay: self.calculateDelay(fadeIn),
					alpha: 0,
					ease: "power3.out"
				})
			}
		}
	},

	handleLineFadeIn(lineFadeIn) {
		const self = this

		const dir = lineFadeIn.getAttribute('dir') == 'fromRight' ? 'right' : 'left'

		if (lineFadeIn !== null) {
			const scrollTriggerEl = lineFadeIn.closest('[animation-group]') ?? lineFadeIn

			const tlLineFadeIn = gsap.timeline({
				scrollTrigger: {
					trigger: scrollTriggerEl,
					start: "top +=" + (window.innerHeight / self.startOffset) + " middle",
					end: "top center",
				}
			})

			tlLineFadeIn.from(lineFadeIn, {
				duration: 1.5,
				delay: self.calculateDelay(lineFadeIn),
				transformOrigin: dir,
				scaleX: 0,
				alpha: 0,
				ease: "power2.inOut",
			})
		}
	},

	handleImgReveal(imgReveal) {
		const self = this

		addElement({
			elementTag: 'span',
			classes: 'img-reveal__overlay',
			appendTo: imgReveal
		})

		const containingSection = imgReveal.closest('section')
		if (imgReveal.hasAttribute('img-reveal-colour')) {
			// If img-reveal-colour is set, use this as the overlay background color
			//
			const overlayColour = imgReveal.getAttribute('img-reveal-colour')
			const overlay = imgReveal.querySelector('.img-reveal__overlay')
			if (overlay !== null) {
				overlay.style.backgroundColor = overlayColour
			}
		} else if (containingSection !== null) {
			let sectionBg = window.getComputedStyle(containingSection).backgroundColor

			// Quickly check if section background is set to transparent or rgba(0, 0, 0, 0),
			// we don't want to make the overlay lighter in this case
			//
			if (sectionBg !== "rgba(0, 0, 0, 0)" && sectionBg !== "transparent") {
				// Lighten the new overlay color and set it on the overlay
				//
				const lightenedBg = lightenColour(sectionBg, 0.08)
				const overlay = imgReveal.querySelector('.img-reveal__overlay')
				if (overlay !== null) {
					overlay.style.backgroundColor = lightenedBg
				}
			}
		}

		if (imgReveal !== null) {
			const scrollTriggerEl = imgReveal.closest('[animation-group]') ?? imgReveal

			const tlImgReveal = gsap.timeline({
				delay: self.calculateDelay(imgReveal),
				scrollTrigger: {
					trigger: scrollTriggerEl,
					start: "top +=" + (window.innerHeight / self.startOffset) + " center",
					end: "top center",
				}
			})

			tlImgReveal.to(imgReveal.querySelector('.img-reveal__overlay'), {
				duration: 1,
				translateY: 0,
				ease: "power4.out"
			})

			tlImgReveal.to(imgReveal.querySelector('.img-reveal__overlay'), {
				duration: 1,
				translateY: '-100%',
				ease: "power4.out",
				onStart: function() {
					imgReveal.querySelector('img').style.opacity = '1'
				}
			}, "-=0.25")

			tlImgReveal.to(imgReveal.querySelector('img'), {
				duration: 1,
				scale: 1,
				ease: "power4.out",
				onComplete: function() {
					if (imgReveal.querySelector('[caption]') !== null) {
						imgReveal.querySelector('[caption]').style.opacity = '1'
					}
				}
			}, "-=1.25")
		}
	},

	calculateDelay(el) {
		const self = this

		// const debugInfo = {
		// 	element: el,
		// 	top: el.getBoundingClientRect().top,
		// 	isInViewport: el.getBoundingClientRect().top < window.innerHeight
		// }
		// console.log(debugInfo)

		// Check if element is in viewport and page change is true (to account for page transitions)
		//
		// Else if, if delay if specificed use this delay, also accepts a custom value
		//
		if (el.getBoundingClientRect().top < window.innerHeight && self.pageChange === true) {
			if (el.hasAttribute('delay') && el.getAttribute('delay') === '') {
				return 0.8 + 0.65
			} else if (el.hasAttribute('delay')) {
				return 0.8 + parseFloat(el.getAttribute('delay'))
			} else {
				return 0.8
			}
		} else if (el.hasAttribute('delay')) {
			if (el.hasAttribute('delay') && el.getAttribute('delay') === '') {
				return 0.65
			} else {
				return parseFloat(el.getAttribute('delay'))
			}
		} else {
			return 0
		}
	}
}

initComponent((pageChange) => {
	animations.init(pageChange, false, true)
})
